import { SheetLabel, SheetPage, SheetSpacer } from "./sheet-chrome";
import type { SheetData } from "./sheet-data";

/**
 * Page 2. The box, as a checklist.
 *
 * Rows name compartments rather than actions ("Compartment 3 - VELUX Windows"),
 * because the reader is standing over the box and the row has to match what is
 * printed on the lid. The Home Hub is where the same module reads as an
 * instruction; both take their names from `MODULE_NAMES`, so the two can differ
 * in tense but never in naming.
 *
 * Only the compartments this household has are printed, and the numbering stays
 * contiguous. There is no closing-task row: placing the sensors is the back half
 * of compartment 2, and its minutes are folded into Air Quality by `buildSheet`.
 */
export function SheetBack({ sheet }: { sheet: SheetData }) {
  return (
    <SheetPage kind="What to expect">
      <h2 className="text-primary mt-[11mm] text-[26pt] leading-[1.12] tracking-[-0.35pt]">
        What&rsquo;s in your box
      </h2>
      <p className="text-muted mt-[4mm] max-w-[128mm] text-[11pt] leading-[1.55]">
        {sheet.boxIntro}
      </p>

      <ul className="bg-card mt-[10mm] overflow-hidden rounded-[3mm]">
        {sheet.compartments.map((compartment) => (
          <li
            key={compartment.key}
            className="border-border-subtle flex items-center gap-[5mm] border-b-[0.25mm] px-[7mm] py-[5.5mm] last:border-b-0"
          >
            {/* A yellow bar, not a numbered badge: the row title now carries
                "Compartment 3", and printing the numeral twice beside it read as
                a stutter. The bar keeps the row rhythm and the brand accent. */}
            <span className="bg-daylight h-[8mm] w-[1.6mm] shrink-0 rounded-full" />
            <span className="text-primary min-w-0 flex-1 text-[13pt] font-bold">
              {compartment.title}
            </span>
            {/* `kit-sheet-shot` is how the route finds these to wait on before
                printing — a shot still loading prints as a gap. */}
            <img
              src={compartment.image}
              alt=""
              className="kit-sheet-shot h-[12mm] w-[22mm] shrink-0 object-contain"
            />
            <span className="text-muted w-[15mm] shrink-0 text-right text-[10pt] tabular-nums">
              {compartment.minutes} min
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-[16mm]">
        <SheetLabel>Have this ready</SheetLabel>
        <ul className="mt-[4mm] flex gap-[6mm]">
          {READY_ITEMS.map((item) => (
            <li
              key={item.title}
              className="bg-surface-subtle flex-1 rounded-[2mm] px-[5mm] py-[4.5mm] text-[10pt] leading-[1.45]"
            >
              <b className="text-primary mb-[1mm] block font-bold">{item.title}</b>
              <span className="text-muted">{item.detail}</span>
            </li>
          ))}
        </ul>
      </div>

      <SheetSpacer />

      <footer className="border-border text-muted flex items-end justify-between border-t-[0.25mm] pt-[5mm] text-[9pt] leading-[1.5]">
        <div>Questions? Ask the team on site.</div>
        <div className="text-right">
          {sheet.belongsTo}
          {sheet.codeDisplay && (
            <>
              <br />
              <b className="text-primary font-bold">Access code {sheet.codeDisplay}</b>
            </>
          )}
        </div>
      </footer>
    </SheetPage>
  );
}

/**
 * What the household needs to hand before starting.
 *
 * Drawn from `network-setup-screen.tsx`, which already asks how many free LAN
 * ports the router has and puts the power strip in compartment 1. Nothing here
 * is invented for the sheet.
 */
const READY_ITEMS = [
  { title: "Your phone", detail: "The whole setup runs in your browser." },
  {
    title: "A power outlet",
    detail: "Near your Wi-Fi router, for the power strip in compartment 1.",
  },
  {
    title: "One free LAN port",
    detail: "On your router. If it has none, use the network switch in compartment 1.",
  },
] as const;
